import React, { useEffect, useRef, useState } from "react";
import { Button, Box, useTheme, Link, IconButton } from "@mui/material";
import CustomLabel from "../components/CustomLabel";
import { COLORS } from "../assets/theme";
import CustomTextField from "../components/CustomTextField";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  requestPasswordResetCode,
  verifyPasswordResetCode,
  resetPasswordWithCode,
} from "../services/authService";

const CODE_LENGTH = 6;
type CodeInputElement = HTMLInputElement | HTMLDivElement;

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [loadingAction, setLoadingAction] = useState<
    "send" | "verify" | "reset" | "resend" | null
  >(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (step !== 2 || resendCooldown <= 0) return;
    const timerId = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timerId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerId);
  }, [step, resendCooldown]);

  const focusCodeInput = (index: number) => {
    const input = codeInputsRef.current[index];
    if (input) {
      input.focus();
      input.setSelectionRange?.(input.value.length, input.value.length);
    }
  };

  const handleCodeInput = (rawValue: string, index: number) => {
    const sanitized = rawValue.replace(/\D/g, "");
    const codeArray = Array.from(
      { length: CODE_LENGTH },
      (_, i) => code[i] || ""
    );

    if (sanitized.length === 0) {
      const hadValue = !!code[index];
      codeArray[index] = "";
      setCode(codeArray.join(""));
      if (!hadValue && index > 0) focusCodeInput(index - 1);
      return;
    }

    let nextIndex = index;
    sanitized.split("").forEach((char) => {
      if (nextIndex < CODE_LENGTH) {
        codeArray[nextIndex] = char;
        nextIndex += 1;
      }
    });

    setCode(codeArray.join(""));

    if (nextIndex < CODE_LENGTH) {
      focusCodeInput(nextIndex);
    } else {
      codeInputsRef.current[CODE_LENGTH - 1]?.blur();
    }
  };

  const handleCodePaste = (
    event: React.ClipboardEvent<CodeInputElement>,
    index: number
  ) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text");
    if (!pasted) return;
    handleCodeInput(pasted, index);
  };

  const handleCodeKeyDown = (
    event: React.KeyboardEvent<CodeInputElement>,
    index: number
  ) => {
    if (event.key === "Backspace" && !code[index] && index > 0) {
      focusCodeInput(index - 1);
    }
  };

  const handleSendCode = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Please enter your email");
      return;
    }
    setError("");
    setStatusMessage("");
    setLoadingAction("send");
    try {
      const response = await requestPasswordResetCode(trimmedEmail);
      if (!response.success) {
        setError(response.error || "Failed to send verification code");
        return;
      }
      setEmail(trimmedEmail);
      setCode("");
      setStatusMessage(response.message || "Verification code sent");
      setStep(2);
      setResendCooldown(60);
    } catch (sendError: any) {
      const apiErrorMessage =
        sendError?.response?.data?.message ||
        sendError?.response?.data?.error ||
        sendError?.message;
      setError(apiErrorMessage || "Failed to send verification code");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleResendCode = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Email is missing. Please enter your email again.");
      setStep(1);
      return;
    }
    setError("");
    setStatusMessage("");
    setLoadingAction("resend");
    try {
      const response = await requestPasswordResetCode(trimmedEmail);
      if (!response.success) {
        setError(response.error || "Failed to resend verification code");
        return;
      }
      setCode("");
      setStatusMessage(
        response.message ||
          "A new verification code was sent. Please use the most recent code."
      );
      setResendCooldown(60);
      setStep(2);
    } catch (resendError: any) {
      const apiErrorMessage =
        resendError?.response?.data?.message ||
        resendError?.response?.data?.error ||
        resendError?.message;
      setError(apiErrorMessage || "Failed to resend verification code");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleVerifyCode = async () => {
    const trimmedEmail = email.trim();
    const trimmedCode = code.trim();
    if (!trimmedEmail) {
      setError("Email is missing. Please enter your email again.");
      setStep(1);
      return;
    }
    if (!trimmedCode || trimmedCode.length !== CODE_LENGTH) {
      setError("Please enter the complete verification code");
      return;
    }
    setError("");
    setStatusMessage("");
    setLoadingAction("verify");
    try {
      const response = await verifyPasswordResetCode(
        trimmedEmail,
        trimmedCode
      );
      if (!response.success) {
        setError(response.error || "Failed to verify code");
        return;
      }
      setCode(trimmedCode);
      setStatusMessage(
        response.message || "Code verified. You can now reset your password."
      );
      setStep(3);
    } catch (verifyError: any) {
      const apiErrorMessage =
        verifyError?.response?.data?.message ||
        verifyError?.response?.data?.error ||
        verifyError?.message;
      setError(apiErrorMessage || "Failed to verify code");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    const trimmedEmail = email.trim();
    const trimmedCode = code.trim();
    if (!trimmedEmail || !trimmedCode) {
      setError(
        "Email or verification code is missing. Please request a new code."
      );
      setStep(1);
      return;
    }
    setError("");
    setStatusMessage("");
    setLoadingAction("reset");
    try {
      const response = await resetPasswordWithCode(
        trimmedEmail,
        trimmedCode,
        newPassword,
        confirmPassword
      );
      if (!response.success) {
        setError(response.error || "Failed to reset password");
        return;
      }
      setStatusMessage(response.message || "Password reset successful!");
      setCode("");
      setNewPassword("");
      setConfirmPassword("");
      setStep(1);
      toast.success(response.message || "Password reset successful!");
      navigate("/login");
    } catch (resetError: any) {
      const apiErrorMessage =
        resetError?.response?.data?.message ||
        resetError?.response?.data?.error ||
        resetError?.message;
      setError(apiErrorMessage || "Failed to reset password");
    } finally {
      setLoadingAction(null);
    }
  };

  const theme = useTheme();
  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          width: { xs: "100%", sm: 400, md: 500 },
          maxWidth: "95vw",
          minHeight: { xs: 400, sm: 500, md: 600 },
          bgcolor: COLORS.WhiteBackground,
          borderRadius: 4,
          boxShadow: 3,
          p: { xs: 2, sm: 3, md: 4 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
      >
        <CustomLabel
          text="Forgot Password"
          fontWeight={700}
          fontSize="2rem"
          color={COLORS.BlackText}
          sx={{ mb: 2 }}
        />
        <Box
          sx={{
            borderBottom: `2px solid ${COLORS.BlackText}`,
            mb: 3,
            width: "80%",
          }}
        />
        <Box sx={{ width: "100%" }}>
          {step === 1 && (
            <>
              <CustomLabel
                text="Email"
                fontWeight={400}
                fontSize="16px"
                color={COLORS.BlackText}
                sx={{ mb: 1 }}
              />
              <CustomTextField
                label=""
                type="email"
                value={email}
                bgColor={COLORS.textFieldBackgroundColor}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                sx={{ mb: 2 }}
                error={!!error}
                helperText={error}
              />
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{
                  borderRadius: 30,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  boxShadow: "0 2px 8px rgba(44, 130, 201, 0.12)",
                  transition: "background 0.2s",
                  background: theme.palette.primary.main,
                  "&:hover": { background: theme.palette.primary.dark },
                }}
                onClick={handleSendCode}
                disabled={loadingAction === "send"}
              >
                <CustomLabel
                  text={
                    loadingAction === "send" ? "Sending..." : "Send Code"
                  }
                  fontWeight={700}
                  fontSize="1.1rem"
                  color="#fff"
                />
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <CustomLabel
                text="Enter Code"
                fontWeight={400}
                fontSize="16px"
                color={COLORS.BlackText}
                sx={{ mb: 1 }}
              />
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  mb: 2,
                  justifyContent: "center",
                }}
              >
                {Array.from({ length: CODE_LENGTH }, (_, i) => (
                  <CustomTextField
                    key={i}
                    label=""
                    type="text"
                    size="small"
                    inputProps={{
                      maxLength: CODE_LENGTH,
                      height: 64,
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      autoComplete: i === 0 ? "one-time-code" : "off",
                      style: {
                        textAlign: "center",
                        fontSize: "2rem",
                        fontWeight: 700,
                        letterSpacing: "2px",
                        padding: 0,
                        borderRadius: "12px",
                        border: "none",
                        background: "transparent",
                        outline: "none",
                        lineHeight: "64px",
                        height: "64px",
                        boxSizing: "border-box",
                      },
                    }}
                    value={code[i] || ""}
                    inputRef={(el) => {
                      codeInputsRef.current[i] = el;
                    }}
                    bgColor={COLORS.textFieldBackgroundColor}
                    onChange={(e) => handleCodeInput(e.target.value, i)}
                    onPaste={(e) => handleCodePaste(e, i)}
                    onKeyDown={(e) => handleCodeKeyDown(e, i)}
                    required
                    error={!!error}
                    sx={{
                      width: 50,
                      minWidth: 0,
                      bgcolor: COLORS.textFieldBackgroundColor,
                      borderRadius: "12px",
                      transition: "border-color 0.2s",
                      fontWeight: 700,
                      textAlign: "center",
                      "& .MuiInputBase-input": {
                        textAlign: "center",
                        fontSize: "2rem",
                        ml: 2,
                        fontWeight: 700,
                        padding: "12px 0",
                        letterSpacing: "2px",
                      },
                    }}
                  />
                ))}
              </Box>
              {error && (
                <Box sx={{ color: "error.main", mb: 2, textAlign: "center" }}>
                  {error}
                </Box>
              )}
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{
                  borderRadius: 30,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  boxShadow: "0 2px 8px rgba(44, 130, 201, 0.12)",
                  transition: "background 0.2s",
                  background: theme.palette.primary.main,
                  "&:hover": { background: theme.palette.primary.dark },
                }}
                onClick={handleVerifyCode}
                disabled={loadingAction === "verify"}
              >
                <CustomLabel
                  text={
                    loadingAction === "verify" ? "Verifying..." : "Verify Code"
                  }
                  fontWeight={700}
                  fontSize="1.1rem"
                  color="#fff"
                />
              </Button>
              <Button
                variant="outlined"
                fullWidth
                sx={{ mt: 2, borderRadius: 30, py: 1.25, fontWeight: 600 }}
                onClick={handleResendCode}
                disabled={resendCooldown > 0 || loadingAction === "resend"}
              >
                <CustomLabel
                  text={
                    loadingAction === "resend"
                      ? "Resending..."
                      : resendCooldown > 0
                      ? `Resend Code (${resendCooldown}s)`
                      : "Resend Code"
                  }
                  fontWeight={600}
                  fontSize="1rem"
                  color={COLORS.BlackText}
                />
              </Button>
            </>
          )}
          {step === 3 && (
            <>
              <CustomLabel
                text="New Password"
                fontWeight={400}
                fontSize="16px"
                color={COLORS.BlackText}
                sx={{ mb: 1 }}
              />
              <CustomTextField
                label=""
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                bgColor={COLORS.textFieldBackgroundColor}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                fullWidth
                sx={{ mb: 2 }}
                error={!!error}
                helperText={error}
                endIcon={
                  <IconButton
                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowNewPassword((prev) => !prev)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
              />
              <CustomLabel
                text="Confirm Password"
                fontWeight={400}
                fontSize="16px"
                color={COLORS.BlackText}
                sx={{ mb: 1 }}
              />
              <CustomTextField
                label=""
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                bgColor={COLORS.textFieldBackgroundColor}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                fullWidth
                sx={{ mb: 2 }}
                error={!!error}
                helperText={error}
                endIcon={
                  <IconButton
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
              />
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{
                  borderRadius: 30,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  boxShadow: "0 2px 8px rgba(44, 130, 201, 0.12)",
                  transition: "background 0.2s",
                  background: theme.palette.primary.main,
                  "&:hover": { background: theme.palette.primary.dark },
                }}
                onClick={handleResetPassword}
                disabled={loadingAction === "reset"}
              >
                <CustomLabel
                  text={
                    loadingAction === "reset"
                      ? "Resetting..."
                      : "Reset Password"
                  }
                  fontWeight={700}
                  fontSize="1.1rem"
                  color="#fff"
                />
              </Button>
            </>
          )}
        </Box>
        {statusMessage && (
          <Box
            sx={{
              mt: 3,
              color: "success.main",
              fontWeight: 600,
              textAlign: "center",
            }}
          >
            {statusMessage}
          </Box>
        )}
        <Link
          href="/login"
          underline="hover"
          sx={{
            fontWeight: 700,
            color: COLORS.BlackText,
            display: "block",
            textAlign: "center",
            mt: 3,
            mb: 2,
          }}
        >
          Back to Login
        </Link>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
